import React from 'react';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  HelpCircle,
  Loader2,
} from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Progress } from './ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';

const AssessmentQuestionScreen = ({
  language,
  assessmentDimensions,
  scoreLabels,
  currentDimension,
  setCurrentDimension,
  answers,
  naAnswers,
  otherText,
  setOtherText,
  handleAnswerChange,
  handleNaChange,
  handleNext,
  handlePrevious,
  submitting,
  error,
}) => {
  const totalDimensions = assessmentDimensions.length;
  const progress = ((currentDimension + 1) / totalDimensions) * 100;
  const currentDimensionData = assessmentDimensions[currentDimension];
  const allQuestionsAnswered = currentDimensionData?.questions.every(
    q => answers[q.id] !== undefined || naAnswers[q.id]
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="py-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Category Navigation Indicator */}
          <div className="mb-4 flex justify-center gap-2 flex-wrap">
            {assessmentDimensions.map((dim, idx) => (
              <button
                key={dim.id}
                type="button"
                onClick={() => setCurrentDimension(idx)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer hover:opacity-80 ${
                  idx === currentDimension
                    ? 'bg-blue-500 text-white shadow-md'
                    : idx < currentDimension
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-200 text-gray-500'
                }`}
              >
                {idx < currentDimension && '✓ '}{dim.title}
              </button>
            ))}
          </div>

          {/* Progress */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-600">
                {language === 'nl' ? 'Categorie' : 'Category'} {currentDimension + 1} {language === 'nl' ? 'van' : 'of'} {totalDimensions}
              </span>
              <span className="text-sm text-gray-500">{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          {/* Question Card */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            {/* Dimension Header */}
            <div className="bg-gradient-to-r from-blue-500 to-blue-700 px-6 py-6 text-white">
              <h2 className="text-2xl font-bold mb-1">{currentDimensionData.title}</h2>
              <p className="text-blue-100 text-sm">{currentDimensionData.description}</p>
            </div>

            {/* Encouraging intro message */}
            {currentDimensionData.intro && (
              <div className="bg-gradient-to-r from-amber-50 to-blue-50 px-6 py-3 border-b border-amber-100">
                <p className="text-amber-800 text-sm font-medium">{currentDimensionData.intro}</p>
              </div>
            )}

            {/* Questions */}
            <div className="p-6 space-y-8">
              {currentDimensionData.questions.map((question, index) => {
                const isNa = naAnswers[question.id] === true;
                const currentValue = answers[question.id];
                const questionText = language === 'nl' ? question.text_nl : question.text_en;
                const tooltipText = language === 'nl' ? question.tooltip_nl : question.tooltip_en;

                // Render qualification questions OR timeframe questions with select
                if ((currentDimensionData.isQualification && question.type === 'select') || question.type === 'timeframe') {
                  const options = language === 'nl' ? question.options_nl : question.options_en;
                  return (
                    <div key={question.id} className="space-y-3">
                      <div className="flex items-start gap-2">
                        <span className="text-blue-500 font-semibold">{index + 1}.</span>
                        <div className="flex-1">
                          <div className="flex items-start gap-2">
                            <p className="text-gray-900 font-medium flex-1">{questionText}</p>
                            {tooltipText && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button className="text-gray-400 hover:text-blue-500 transition-colors">
                                      <HelpCircle className="h-4 w-4" />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs">
                                    <p className="text-sm">{tooltipText}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                        </div>
                      </div>
                      <Select
                        value={currentValue !== undefined ? String(currentValue) : ''}
                        onValueChange={(value) => handleAnswerChange(question.id, value)}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={language === 'nl' ? 'Selecteer een optie' : 'Select an option'} />
                        </SelectTrigger>
                        <SelectContent>
                          {options.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                }

                // Render multiselect questions (checkboxes)
                if (question.type === 'multiselect') {
                  const options = language === 'nl' ? question.options_nl : question.options_en;
                  const selectedValues = Array.isArray(currentValue) ? currentValue : [];
                  const showOtherInput = selectedValues.includes('other');
              
                  const handleMultiSelectChange = (optionValue, checked) => {
                    let newValues;
                    if (checked) {
                      newValues = [...selectedValues, optionValue];
                    } else {
                      newValues = selectedValues.filter(v => v !== optionValue);
                      // Clear other text if unchecking "other"
                      if (optionValue === 'other') {
                        setOtherText(prev => {
                          const newOther = { ...prev };
                          delete newOther[question.id];
                          return newOther;
                        });
                      }
                    }
                    handleAnswerChange(question.id, newValues);
                  };

                  return (
                    <div key={question.id} className="space-y-3">
                      <div className="flex items-start gap-2">
                        <span className="text-blue-500 font-semibold">{index + 1}.</span>
                        <div className="flex-1">
                          <div className="flex items-start gap-2">
                            <p className="text-gray-900 font-medium flex-1">{questionText}</p>
                            {tooltipText && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button className="text-gray-400 hover:text-blue-500 transition-colors">
                                      <HelpCircle className="h-4 w-4" />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs">
                                    <p className="text-sm">{tooltipText}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {language === 'nl' ? '(meerdere opties mogelijk)' : '(multiple options allowed)'}
                          </p>
                        </div>
                      </div>
                      <div className="space-y-2 pl-6">
                        {options.map((opt) => (
                          <div key={opt.value}>
                            <label className="flex items-center gap-3 cursor-pointer group">
                              <input
                                type="checkbox"
                                checked={selectedValues.includes(opt.value)}
                                onChange={(e) => handleMultiSelectChange(opt.value, e.target.checked)}
                                className="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
                              />
                              <span className="text-gray-700 group-hover:text-gray-900">
                                {opt.value === 'other' 
                                  ? (language === 'nl' ? 'Anders, namelijk:' : 'Other, namely:')
                                  : opt.label}
                              </span>
                            </label>
                            {/* Show text input when "Other" is selected */}
                            {opt.value === 'other' && showOtherInput && (
                              <div className="mt-2 ml-7">
                                <input
                                  type="text"
                                  value={otherText[question.id] || ''}
                                  onChange={(e) => setOtherText(prev => ({ ...prev, [question.id]: e.target.value }))}
                                  placeholder={language === 'nl' ? 'Vul hier uw antwoord in...' : 'Enter your answer here...'}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }

                // Regular Likert scale question
                return (
                  <div key={question.id} className="space-y-4">
                    {/* Question text with tooltip */}
                    <div className="flex items-start gap-2">
                      <span className="text-blue-500 font-semibold">{index + 1}.</span>
                      <div className="flex-1">
                        <div className="flex items-start gap-2">
                          <p className="text-gray-900 font-medium flex-1">{questionText}</p>
                          {tooltipText && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button className="text-gray-400 hover:text-blue-500 transition-colors">
                                    <HelpCircle className="h-4 w-4" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  <p className="text-sm">{tooltipText}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* 6-point Likert scale */}
                    <div className={`space-y-3 ${isNa ? 'opacity-50' : ''}`}>
                      <div className="grid grid-cols-6 gap-2">
                        {[1, 2, 3, 4, 5, 6].map((value) => (
                          <button
                            key={value}
                            onClick={() => !isNa && handleAnswerChange(question.id, value)}
                            disabled={isNa}
                            className={`py-3 px-2 rounded-lg text-sm font-medium transition-all ${
                              currentValue === value
                                ? 'bg-blue-500 text-white shadow-md'
                                : 'bg-gray-100 text-gray-600 hover:bg-blue-100'
                            } ${isNa ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                          >
                            {value}
                          </button>
                        ))}
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 px-1">
                        <span>{scoreLabels[0]}</span>
                        <span>{scoreLabels[5]}</span>
                      </div>
                    </div>

                    {/* Not Applicable checkbox - hide if question.hideNA is true */}
                    {!question.hideNA && (
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id={`na-${question.id}`}
                          checked={isNa}
                          onChange={(e) => handleNaChange(question.id, e.target.checked)}
                          className="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <label htmlFor={`na-${question.id}`} className="text-sm text-gray-500">
                          {language === 'nl' ? 'Niet van toepassing' : 'Not applicable'}
                        </label>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Navigation */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-between">
              <Button
                variant="outline"
                onClick={handlePrevious}
                disabled={currentDimension === 0}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                {language === 'nl' ? 'Vorige' : 'Previous'}
              </Button>
              <Button
                onClick={handleNext}
                disabled={!allQuestionsAnswered || submitting}
                className="bg-blue-500 hover:bg-blue-600"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {language === 'nl' ? 'Bezig...' : 'Processing...'}
                  </>
                ) : currentDimension === totalDimensions - 1 ? (
                  <>
                    {language === 'nl' ? 'Bekijk Resultaten' : 'View Results'}
                    <CheckCircle className="ml-2 h-4 w-4" />
                  </>
                ) : (
                  <>
                    {language === 'nl' ? 'Volgende' : 'Next'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </div>

            {error && (
              <div className="px-6 pb-4">
                <p className="text-red-500 text-sm text-center">{error}</p>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AssessmentQuestionScreen;
